import { Component, OnInit, ViewChild, Input, OnChanges, AfterViewInit, Inject, Output, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions, MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { NgForm, UntypedFormBuilder, UntypedFormGroup, Validators, AbstractControl, FormControl } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { map, merge, take } from 'rxjs/operators';
import { AlertService, MessageSeverity } from '../../../services/alert.service';
import { AppTranslationService } from '../../../services/app-translation.service';
import { ReportStandardViewModel, ReportService } from '../../index';
import { AuthService } from '../../../services/auth.service';
import { Permission } from '../../../models/permission.model';
import { Utilities } from '../../../services/utilities';

@Component({
    selector: 'app-report-standard-details',
    templateUrl: './report-standard-details.component.html',
    styleUrls: ['./report-standard-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ReportStandardDetailsComponent implements OnInit {

    @ViewChild('form', { static: true }) form: NgForm;

    reportStandardForm: UntypedFormGroup;

    reportStandardId: number;
    reportStandardName: string;
    reportId: number;

    loadingIndicator: boolean = false;
    private isSaving: boolean = false;
    anySaveOperationsPerformed: boolean = false;
    hasPermissionToChange$: Observable<boolean>;

    constructor(
        public dialogRef: MatDialogRef<ReportStandardDetailsComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private alertService: AlertService,
        private translationService: AppTranslationService,
        private authService: AuthService,
        private reportService: ReportService,
        private formBuilder: UntypedFormBuilder,
        private snackBar: MatSnackBar,
        public dialog: MatDialog
    ) {
        this.reportStandardId = data.reportStandardId;
        this.reportStandardName = data.reportStandardName;
        this.reportId = data.reportId;
        this.hasPermissionToChange$ = data.hasPermissionToChange$;
    }

    ngOnInit(): void {
        this.buildForm();
        this.loadData(this.reportStandardId);

        // Use this construct to handle the escape key for cancelling
        this.dialogRef.keydownEvents().pipe(map(r => new EventMapper(r, this))).subscribe((e) => {
            if (e.event.key === "Escape") {
                e.component.cancel();
            }
        }
        );

    }

    public loadData(reportStandardId: number): void {
        this.alertService.startLoadingMessage();
        this.loadingIndicator = true;
        this.reportService.getReportStandardById(reportStandardId).subscribe(ok => this.dataLoaded(ok),
            error => this.onDataLoadFailed(error));
    }

    dataLoaded(reportStandard: ReportStandardViewModel) {
        this.alertService.stopLoadingMessage();
        this.loadingIndicator = false;
        this.resetForm(reportStandard);
    }

    private onDataLoadFailed(error: any) {
        this.alertService.stopLoadingMessage();
        this.loadingIndicator = false;

        this.alertService.showStickyMessage(this.translationService.getTranslation("Error"),
            this.translationService.getTranslation("FetchErrorMessage") +
            "\r\n" +
            Utilities.getHttpResponseMessage(error),
            MessageSeverity.error,
            error);
    }


    public getFormControl(name: string): AbstractControl {
        return this.reportStandardForm.controls[name];
    }

    private buildForm() {
        this.reportStandardForm = this.formBuilder.group({
            reportStandardId: '',
            reportId: '',
            name: '',
            description: [{ value: '', disabled: true }],
            suggestions: '',
            noOfSuggestions: ['', Validators.compose([Validators.min(0), Validators.max(99)])],
            internalNotes: '',
            fulfilled: '',
            previousFulfilled: [{ value: '', disabled: true }],
            previousSuggestions: [{ value: '', disabled: true }],
        });
    }

    public resetForm(reportStandard: ReportStandardViewModel) {
        this.reportStandardForm.reset({
            reportStandardId: reportStandard.reportStandardId,
            name: reportStandard.name,
            description: reportStandard.description,
            suggestions: reportStandard.suggestions,
            noOfSuggestions: reportStandard.noOfSuggestions,
            internalNotes: reportStandard.internalNotes,
            fulfilled: reportStandard.fulfilled,
            previousFulfilled: reportStandard.previousFulfilled,
            previousSuggestions: reportStandard.previousSuggestions || '',
            reportId: reportStandard.reportId
        });
    }

    private prepareSave(): ReportStandardViewModel {
        const formModel = this.reportStandardForm.value;

        return {
            reportStandardId: formModel.reportStandardId,
            name: formModel.name,
            description: formModel.description,
            suggestions: formModel.suggestions,
            noOfSuggestions: formModel.noOfSuggestions,
            internalNotes: formModel.internalNotes,
            fulfilled: formModel.fulfilled,
            previousFulfilled: formModel.previousFulfilled,
            reportId: formModel.reportId
        };
    }

    public save(close: boolean) {
        if (!this.reportStandardForm.valid) {
            this.alertService.showValidationError();
            return;
        }

        this.isSaving = true;
        this.alertService.startLoadingMessage(this.translationService.getTranslation("globalTexts.SavingMessage"));

        const reportStandard = this.prepareSave();

        let reportStandards: ReportStandardViewModel[] = [];
        reportStandards.push(reportStandard);

        this.reportService.updateReportStandards(reportStandards).subscribe(
            t => this.saveCompleted(close, t[0]),
            error => this.saveFailed(error));
    }

    public cancel() {
        this.alertService.resetStickyMessage();

        if (this.reportStandardForm.dirty) {
            this.snackBar.open(this.translationService.getTranslation("globalTexts.DirtyFormBackQuery"),
                this.translationService.getTranslation("commands.OK"),
                { duration: 5000 })
                .onAction().subscribe(() => {
                    this.dialogRef.close(this.anySaveOperationsPerformed);
                }
                );
        } else {
            this.dialogRef.close(this.anySaveOperationsPerformed);
        }
    }

    private saveCompleted(close: boolean, reportStandard: ReportStandardViewModel) {

        this.isSaving = false;
        this.anySaveOperationsPerformed = true;
        this.alertService.stopLoadingMessage();
        this.alertService.showMessage(this.translationService.getTranslation("commands.Save"), reportStandard.name + " " + this.translationService.getTranslation("globalTexts.saved"), MessageSeverity.success);
        if (close) {
            this.dialogRef.close(true);
        } else {
            this.resetForm(reportStandard);
        }
    }

    private saveFailed(error: any) {
        this.isSaving = false;
        this.alertService.stopLoadingMessage();
        this.alertService.showStickyMessage(this.translationService.getTranslation("globalTexts.CouldNotSave"), this.translationService.getTranslation("globalTexts.SaveErrorMessage"), MessageSeverity.error, error);
        this.alertService.showStickyMessage(error, null, MessageSeverity.error);
    }

    hasPermissionToChange(): boolean {
        return this.authService.userHasPermission(Permission.changeReports);
    }

}

export class EventMapper {
    component: ReportStandardDetailsComponent;
    event: KeyboardEvent;

    constructor(event: KeyboardEvent, component: ReportStandardDetailsComponent) {
        this.component = component;
        this.event = event;
    }
}

